'use client';

import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import styles from '../styles/Home.module.css';
import { useTranslation, buildUrlWithLocale } from '@/lib/i18n';

export default function Page() {
  const router = useRouter();
  const { t, locale } = useTranslation();
  const [joinCode, setJoinCode] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const createMeeting = async () => {
    setIsCreating(true);
    setError('');

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'https://api.maatrics.com';
      const res = await fetch(`${apiUrl}/api/meetings/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || t('home.meetingCreateFailed'));
      }

      router.push(buildUrlWithLocale(`/join/${data.hash}`, locale));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('home.errorDefault'));
      setIsCreating(false);
    }
  };

  const handleJoin = () => {
    if (!joinCode.trim()) return;

    let hash = joinCode.trim();

    // Extract hash from full URL if provided
    if (hash.includes('/join/')) {
      const parts = hash.split('/join/');
      hash = parts[parts.length - 1];
    }

    // Remove any trailing slashes or query params
    hash = hash.split('?')[0].split('#')[0].replace(/\/$/, '');

    if (hash) {
      router.push(buildUrlWithLocale(`/join/${hash}`, locale));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && joinCode.trim()) {
      handleJoin();
    }
  };

  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className="header">
          <img
            src="https://app.maatrics.com/imgs/logo/dark.png"
            alt="Maatrics"
            style={{ height: '48px', marginBottom: '1rem' }}
          />
          <h2 style={{ opacity: 0.7, fontWeight: 400, fontSize: '1.1rem' }}>
            {t('home.title')}
          </h2>
        </div>

        <div className={styles.tabContainer}>
          <div className={styles.tabContent}>
            <button
              className="lk-button"
              onClick={createMeeting}
              disabled={isCreating}
              style={{
                padding: '1rem 2rem',
                fontSize: '1rem',
                fontWeight: 500,
              }}
            >
              {isCreating ? t('home.creating') : t('home.createMeeting')}
            </button>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                margin: '1rem 0',
              }}
            >
              <hr style={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.15)' }} />
              <span style={{ opacity: 0.5, fontSize: '0.875rem' }}>{t('home.or')}</span>
              <hr style={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.15)' }} />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder={t('home.joinPlaceholder')}
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                }}
              />
              <button
                className="lk-button"
                onClick={handleJoin}
                disabled={!joinCode.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  opacity: joinCode.trim() ? 1 : 0.5,
                }}
              >
                {t('home.join')}
              </button>
            </div>

            {error && (
              <p style={{ color: '#ff6b6b', margin: 0, fontSize: '0.875rem' }}>{error}</p>
            )}
          </div>
        </div>
      </main>

      <footer data-lk-theme="default" style={{ opacity: 0.5 }}>
        {t('home.poweredBy')}{' '}
        <a href="https://maatrics.com" rel="noopener">
          Maatrics
        </a>
      </footer>
    </>
  );
}
